import { ICustomServiceResponse } from "@lcs/frontend";
import { AxiosError, AxiosInstance, AxiosResponse } from "axios";
import { LinkedResource } from "./common.types";
import { BE_PREFIX } from "../utils/Constants";


export interface IStartDate {
    value: string;
}
export interface IProblem {
    id: number | null;
    denominazione?: string;
    nome?: string;
    idAzienda?: number;	
    startDate?: string;
}

export interface AppointmentProblem {
    dipendenti: Dipendente[];
    appuntamenti: any;    	
    startDate: IStartDate;
    score: any;
    scores: any;
}

export interface DefinedProblem {
    id: number;
    denominazione: string;
    nome: string;   
    idAzienda: LinkedResource;	
    startDate: Date;  
    planningDays: number;  
}

export interface DefinedProblemResponse extends ICustomServiceResponse{
    data: DefinedProblem[];
}

export interface SubmitRequest {
    problemName: string; // "Appuntamento" (di fantasia)
	problemDescription: string; // "APPOINTMENT_MANAGEMENT" (fisso)
	rulesConfig: any[] | undefined; // null
	problem: any;  // stringify AppointmentProblem (da export)
	terminationConfig: any; // null
}

export interface LastSubmittedRequest {
    problemName: string;
}

export interface QuerySolutionRequest{
    problemId: number,
    transformArgs: any // null
}

export interface QuerySolutionResponse extends ICustomServiceResponse{
    problemId: number,
	finished: boolean,
	solution: string,
	problemClassName: string,
	problemName: string,
	submitDate: Date,
	solveDate: Date
}

export interface ProblemStatus {
    problemId: number,
	problemName?: string,
	finished?: boolean,
	monitoring?: boolean,
	submitDate?: Date,
	solveDate?: Date
}

export interface LastSubmittedResponse extends ICustomServiceResponse{
    problemStatus: ProblemStatus;
}

export interface NextAppuntamento{
    nextAppuntamento: NextAppuntamento | undefined;
    entityId: number;
    durataStimataInMezzore: number,
    mezzoraDInizio: number,
    cliente: string,
    tipoCliente?: TipoCliente,
    dipendente?: NomeCognome,
    sede?: Sede,
    argomento?: Argomento
}

export interface Argomento{
    descrizione: string
}

export interface TipoCliente{
    descrizione: string
}

export interface NomeCognome {
    nome: string;
    cognome: string;
}

export interface Sede{
    citta: string
}

export interface Dipendente{
    entityId: number,
    mezzoraDiFine: number,
    nextAppuntamento: NextAppuntamento,
    nomeCognome: NomeCognome,
    notWorkingRanges: any[],
    sede: Sede,
    valutazioni: any[]
}

class ProblemService {
    readonly axios : AxiosInstance;
    PLANNING_BASE_PATH: string;
    APPOINTMENT_BASE_PATH: string;

    constructor(axios: AxiosInstance) {
        this.axios = axios; // con base path sull'AppointmentService

        this.PLANNING_BASE_PATH = (process.env.REACT_APP_BE_URL??"") + 
                                 BE_PREFIX.PLANNING;
        this.APPOINTMENT_BASE_PATH = (process.env.REACT_APP_BE_URL??"") + 
                                 BE_PREFIX.APPOINTMENT;      
    }

    getDefinedProblem = () : Promise<DefinedProblemResponse> => {
        return this.axios
            .get(this.APPOINTMENT_BASE_PATH + "/api/data/problema")
            .then((result : AxiosResponse<DefinedProblemResponse>) => result.data);
    }

    export = (problem: IProblem) : Promise<any> => {
        return this.axios
            .post(this.APPOINTMENT_BASE_PATH + "/api/behavior/exportProblema", problem)
            .then((result : AxiosResponse<any>) => {
                return result.data})
            .catch((error: AxiosError) => {
            const res :LastSubmittedResponse = {
                problemStatus: {problemId: 0},
                success: false,
                reason: "Generic error"
            };
            return res;
        });
    }

    submit = (submitReq: SubmitRequest) : Promise<ICustomServiceResponse> => {
        return this.axios
            .post(this.PLANNING_BASE_PATH + '/planning/submit', submitReq)
            .then((result : AxiosResponse<ICustomServiceResponse>) => {
                return result.data})
            .catch(() => {
                const res :LastSubmittedResponse = {
                    problemStatus: {problemId: 0},
                    success: false,
                    reason: "Generic error"
                };
                return res;
            });
    }

    getLastSubmittedProblem = (lsRequest: LastSubmittedRequest) : Promise<LastSubmittedResponse> => {
        return this.axios
        .post(this.PLANNING_BASE_PATH + '/planning/getLastSubmitted', lsRequest)
        .then((result : AxiosResponse<LastSubmittedResponse>) => result.data)
        .catch(() => {
            const res :LastSubmittedResponse = {
                problemStatus: {problemId: 0},
                success: false,
                reason: "Generic error"
            };
            return res;
        });
    }

    querySolution = (qsRequest: QuerySolutionRequest) : Promise<QuerySolutionResponse> => {
        return this.axios
            .post(this.PLANNING_BASE_PATH + '/planning/querySolution', qsRequest)
            .then((result : AxiosResponse<QuerySolutionResponse>) => result.data);
    }

}

export default ProblemService;